import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { OrgLabelsProvider } from "./contexts/OrgLabelsContext";
import { PendingTasksProvider } from "./contexts/PendingTasksContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { Toaster } from "./components/ui/sonner";
import { protectedRoutes } from "./config/routes";
import { MobileBottomNav } from "./components/layout/MobileBottomNav";
import { PWAInstallPrompt } from "./components/PWAInstallPrompt";

// Pages (public + unprotected)
import Login from "./pages/Login";
import AccountSetup from "./pages/AccountSetup";
import TimeTrackingTerminal from "./pages/TimeTrackingTerminal";
import ShiftsTVView from "./pages/ShiftsTVView";
import Careers from "./pages/Careers";
import CareersJob from "./pages/CareersJob";
import SignPublic from "./pages/SignPublic";
import NotFound from "./pages/NotFound";
import { getKioskPath } from "./lib/kioskMode";

const ProtectedRoute = ({ children, adminOnly = false, permArea = null, permLevel = 'view' }) => {
  const { isAuthenticated, isAdmin, hasPermission, loading, mustSetup } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (mustSetup) return <Navigate to="/setup" replace />;
  if (adminOnly && !isAdmin) return <Navigate to="/dashboard" replace />;
  if (permArea && !hasPermission(permArea, permLevel)) return <Navigate to="/dashboard" replace />;
  return children;
};

const SetupRoute = ({ children }) => {
  const { isAuthenticated, loading, mustSetup } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!mustSetup) return <Navigate to="/dashboard" replace />;
  return children;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Kiosk-Lock: wenn dieser Tab als TV/Terminal markiert ist, darf die
  // Login-Seite nicht sichtbar sein — direkt zurueck zur Kiosk-Ansicht.
  const kioskPath = getKioskPath();
  if (kioskPath) return <Navigate to={kioskPath} replace />;

  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />

      {/* Account Setup (first login) */}
      <Route path="/setup" element={<SetupRoute><AccountSetup /></SetupRoute>} />

      {/* Unprotected (Terminal / TV) */}
      <Route path="/zeiterfassung/terminal" element={<TimeTrackingTerminal />} />
      <Route path="/zeiterfassung/terminal/:locationId" element={<TimeTrackingTerminal />} />
      <Route path="/einsatzplan/tv/:locationId" element={<ShiftsTVView />} />

      {/* Öffentliche Karriereseite (ohne Login) */}
      <Route path="/karriere" element={<Careers />} />
      <Route path="/karriere/:jobId" element={<CareersJob />} />
      <Route path="/signieren/:token" element={<SignPublic />} />

      {/* Protected Routes (from config) */}
      {protectedRoutes.map(({ path, element: Element, adminOnly, permArea, permLevel }) => (
        <Route
          key={path}
          path={path}
          element={
            <ProtectedRoute adminOnly={adminOnly} permArea={permArea} permLevel={permLevel}>
              <Element />
            </ProtectedRoute>
          }
        />
      ))}

      {/* Default Redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      {/* Catch-All: zeigt 404 ohne Login-Hinweis. NotFound leitet automatisch
          zurueck zur Kiosk-Seite, falls dieser Tab als TV/Terminal markiert ist. */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <OrgLabelsProvider>
          <PendingTasksProvider>
            <BrowserRouter>
              <AppRoutes />
              <MobileBottomNavWrapper />
              <PWAInstallPromptGate />
              <Toaster position="top-right" richColors />
            </BrowserRouter>
          </PendingTasksProvider>
        </OrgLabelsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

function PWAInstallPromptGate() {
  const { isAuthenticated } = useAuth();
  // Nur fuer eingeloggte Nutzer und nicht auf den oeffentlichen Terminals/TV
  const blockedPaths = ['/login', '/setup', '/zeiterfassung/terminal'];
  const isPublic = blockedPaths.some(p => window.location.pathname.startsWith(p)) || window.location.pathname.includes('/tv/');
  if (!isAuthenticated || isPublic) return null;
  return <PWAInstallPrompt />;
}

function MobileBottomNavWrapper() {
  const { isAuthenticated } = useAuth();
  const publicPaths = ['/login', '/setup', '/zeiterfassung/terminal'];
  const isPublicPage = publicPaths.some(p => window.location.pathname.startsWith(p)) || window.location.pathname.includes('/tv/');
  if (!isAuthenticated || isPublicPage) return null;
  return <MobileBottomNav />;
}

export default App;
